import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/**
 * @param {string} productPageUrl
 * @returns {Promise<Element | null>} Cloned `[data-product-grid-content]` from the fetched PDP.
 */
export async function fetchProductGridCloneForQuickAdd(productPageUrl) {
  try {
    const response = await fetch(productPageUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) return null;
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = html.querySelector('[data-product-grid-content]');
    return grid ? /** @type {Element} */ (grid.cloneNode(true)) : null;
  } catch {
    return null;
  }
}

/**
 * Reorders PDP fragments on small viewports to match quick-add layout (same as QuickAddComponent).
 * @param {Element} productGrid
 * @param {string} productPageUrl
 * @param {string} productTitle
 * @returns {Element}
 */
export function prepareProductGridCloneForQuickAddModal(productGrid, productPageUrl, productTitle) {
  if (!isMobileBreakpoint()) return productGrid;

  const productDetails = productGrid.querySelector('.product-details');
  const productFormComponent = productGrid.querySelector('product-form-component');
  const variantPicker = productGrid.querySelector('variant-picker');
  const productPrice = productGrid.querySelector('product-price');
  const productTitleEl = document.createElement('a');
  productTitleEl.textContent = productTitle;
  productTitleEl.href = productPageUrl;

  const productHeader = document.createElement('div');
  productHeader.classList.add('product-header');

  productHeader.appendChild(productTitleEl);
  if (productPrice) {
    productHeader.appendChild(productPrice);
  }
  productGrid.appendChild(productHeader);

  if (variantPicker) {
    productGrid.appendChild(variantPicker);
  }
  if (productFormComponent) {
    productGrid.appendChild(productFormComponent);
  }

  productDetails?.remove();
  return productGrid;
}

/**
 * @param {Element} productGridClone - Fresh clone; used only to read title for the modal chrome.
 */
export function setQuickAddModalHeadingFromGridClone(productGridClone) {
  const headingEl = document.getElementById('quick-add-modal-heading');
  const innerDialog = document.querySelector('#quick-add-dialog dialog.quick-add-modal');
  if (!headingEl) return;

  let titleText = '';
  const titleLink = productGridClone.querySelector('.view-product-title a');
  if (titleLink) titleText = (titleLink.textContent || '').trim();
  if (!titleText) {
    const h1 = productGridClone.querySelector('h1');
    if (h1) titleText = (h1.textContent || '').trim();
  }

  headingEl.textContent = titleText;
  if (titleText && innerDialog) {
    innerDialog.setAttribute('aria-labelledby', 'quick-add-modal-heading');
  } else {
    innerDialog?.removeAttribute('aria-labelledby');
  }
}

/**
 * @param {HTMLElement | null | undefined} modalContent
 * @param {string} [variantId]
 */
export function syncQuickAddModalVariantFromId(modalContent, variantId) {
  if (!modalContent || !variantId) return;
  const idInput = modalContent.querySelector('input[name="id"]');
  const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
  for (const input of modalInputs) {
    if (input instanceof HTMLInputElement && input.dataset.variantId === variantId && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      if (idInput instanceof HTMLInputElement) idInput.value = String(variantId);
      break;
    }
  }

  const pickerSelect = modalContent.querySelector('variant-picker select');
  if (pickerSelect instanceof HTMLSelectElement) {
    const matchingOption = [...pickerSelect.options].find((opt) => opt.dataset.variantId === variantId);
    if (matchingOption && pickerSelect.value !== matchingOption.value) {
      pickerSelect.value = matchingOption.value;
      pickerSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  if (idInput instanceof HTMLInputElement) idInput.value = String(variantId);
}

/**
 * @param {Element} productGridPrepared
 */
export function morphIntoQuickAddModal(productGridPrepared) {
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent) return;
  morph(modalContent, productGridPrepared);
}

export function showQuickAddDialogWithScrollReset() {
  const dialogComponent = document.getElementById('quick-add-dialog');
  if (!dialogComponent || typeof dialogComponent.showDialog !== 'function') return;

  const dialog = dialogComponent.refs?.dialog;
  if (dialog) {
    dialog.addEventListener(
      'animationstart',
      () => {
        const productDetails = dialogComponent.querySelector('.product-details');
        const productMedia = dialogComponent.querySelector('.product-information__media');
        productDetails?.scrollTo({ top: 0, behavior: 'instant' });
        productMedia?.scrollTo({ top: 0, behavior: 'instant' });
      },
      { once: true }
    );
  }

  dialogComponent.showDialog();
}

/** @type {SVGElement | null} */
let bunningsQuickaddAtcSvgSource = null;

/** @type {Promise<void>} */
let bunningsQuickaddAtcApplyChain = Promise.resolve();

/**
 * Strip PDP quick-add markup down to variant picker + add to cart (kiosk-style).
 * @param {HTMLElement | null | undefined} modalContent
 */
export function pruneQuickAddModalToKioskMinimal(modalContent) {
  if (!modalContent) return;
  modalContent.classList.add('kiosk-quickadd--minimal');
  const root = modalContent.querySelector('[data-product-grid-content]') || modalContent;
  const removeSelectors = [
    '.product-information__media',
    'product-recommendations',
    '.product-recommendations-wrapper',
    '.bunnings-product-details__header',
    '.view-product-title',
    '.product-header',
    'product-price',
    '.view-more-details__wrapper',
    'volume-pricing',
    '.product-information__breadcrumbs-wrapper',
    '.accelerated-checkout-block',
    'local-pickup',
    '.product__pickup-availabilities',
    '.buy-buttons-block__product-description',
    '.quantity-selector-wrapper',
    '.quantity-label',
    '.quantity-rules',
  ];
  removeSelectors.forEach((sel) => {
    root.querySelectorAll(sel).forEach((el) => el.remove());
  });

  const groupContent = root.querySelector('.product-details .group-block-content');
  if (groupContent) {
    [...groupContent.children].forEach((el) => {
      const hasVariant = el.matches('variant-picker') || Boolean(el.querySelector?.('variant-picker'));
      const hasForm = el.matches('product-form-component') || Boolean(el.querySelector?.('product-form-component'));
      if (hasVariant || hasForm) return;
      el.remove();
    });
  }
}

/**
 * Keep the product form hidden variant id in sync with the variant picker.
 * @param {HTMLElement | null | undefined} modalContent
 */
export function wireQuickAddVariantIdSync(modalContent) {
  if (!modalContent) return;
  const picker = modalContent.querySelector('variant-picker');
  const productForm = modalContent.querySelector('product-form-component');
  const formEl = productForm?.querySelector?.('form');
  if (!picker || !formEl) return;

  const syncVariantIdFromPicker = () => {
    const checkedRadio = picker.querySelector('fieldset input[type="radio"]:checked');
    let vid = checkedRadio?.dataset?.variantId;
    if (!vid) {
      const select = picker.querySelector('select');
      const opt = select?.selectedOptions?.[0];
      vid = opt?.dataset?.variantId;
    }
    const idInput = formEl.querySelector('input[name="id"]');
    if (vid && idInput) idInput.value = String(vid);
  };

  if (picker.getAttribute('data-quickadd-variant-sync-bound') !== 'true') {
    picker.addEventListener('change', syncVariantIdFromPicker);
    picker.setAttribute('data-quickadd-variant-sync-bound', 'true');
  }
  if (formEl.getAttribute('data-quickadd-variant-submit-sync-bound') !== 'true') {
    formEl.addEventListener('submit', syncVariantIdFromPicker);
    formEl.setAttribute('data-quickadd-variant-submit-sync-bound', 'true');
  }
  syncVariantIdFromPicker();
}

async function ensureBunningsQuickaddAtcSvgSource() {
  if (bunningsQuickaddAtcSvgSource) return bunningsQuickaddAtcSvgSource;
  const dialog = document.getElementById('quick-add-dialog');
  const iconUrl = (dialog?.dataset?.bunningsQuickaddAtcIconUrl || '').trim();
  if (!iconUrl) return null;
  try {
    const svgText = await fetch(iconUrl, { credentials: 'same-origin' }).then((r) => {
      if (!r.ok) throw new Error(String(r.status));
      return r.text();
    });
    const parsed = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    if (parsed.querySelector('parsererror')) throw new Error('invalid svg');
    const svgEl =
      parsed.documentElement?.tagName?.toLowerCase() === 'svg'
        ? parsed.documentElement
        : parsed.querySelector('svg');
    if (!(svgEl instanceof SVGElement)) throw new Error('no svg root');
    bunningsQuickaddAtcSvgSource = /** @type {SVGElement} */ (document.importNode(svgEl, true));
  } catch (e) {
    console.warn('[quick-add] Bunnings kiosk-style ATC icon failed', e);
    return null;
  }
  return bunningsQuickaddAtcSvgSource;
}

/**
 * Ensure the choose-variant modal ATC uses the Bunnings cart-add icon plus the
 * visible "Add to cart" label (same treatment as product tiles). Re-run after
 * variant morph, which can replace the button markup.
 * @param {HTMLElement | null | undefined} modalContent
 */
export async function applyBunningsQuickAddKioskAtcIcon(modalContent) {
  if (!modalContent) return;
  const btn = modalContent.querySelector('.buy-buttons-block .add-to-cart-button');
  if (!btn) return;

  const labelWrap = btn.querySelector('.add-to-cart-text');
  const textContent = btn.querySelector('.add-to-cart-text__content');
  if (!labelWrap) return;

  btn.querySelectorAll('.add-to-cart-icon').forEach((el) => el.remove());
  btn.classList.remove('kiosk-quickadd-atc--icon-only');
  if (textContent) textContent.classList.remove('kiosk-atc-label-visually-hidden');

  const svgSource = await ensureBunningsQuickaddAtcSvgSource();
  if (!svgSource) return;

  const icon = document.createElement('span');
  icon.className = 'svg-wrapper add-to-cart-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.appendChild(svgSource.cloneNode(true));
  labelWrap.insertBefore(icon, labelWrap.firstChild);

  btn.querySelectorAll('.add-to-cart-icon').forEach((el, idx) => {
    if (idx > 0) el.remove();
  });
}

function queueBunningsQuickAddKioskAtcIcon(modalContent) {
  bunningsQuickaddAtcApplyChain = bunningsQuickaddAtcApplyChain
    .catch(() => {})
    .then(() => applyBunningsQuickAddKioskAtcIcon(modalContent));
}

/**
 * Enforces the project availability rule (docs/business-rules/product-availability.md)
 * on the inner add-to-cart button of the quick-add modal. Reads the selected variant
 * from the modal's variant-picker JSON and drives BOTH `disabled` and `data-unavailable`
 * on the ATC button to match the current variant's availability.
 *
 * Why both attributes, and why bidirectional:
 *  - `product-form.js` #onVariantUpdate awaits `#fetchAndUpdateCartQuantity()` then runs
 *    `#setAddToCartLoading(false)` in a `finally` block, which calls
 *    `setAddToCartButtonEnabled(btn, true)`. That helper ONLY honours `data-unavailable`
 *    as the "stay disabled" gate — `disabled` alone is cleared. So to keep a sold-out or
 *    $0 variant's ATC disabled through the async re-enable, we MUST set `data-unavailable`.
 *  - `assets/morph.js` uses `childrenOnly: true`, so `#onVariantUpdate` copies `disabled`
 *    and `data-unavailable` from the server response itself. This enforcer still CLEARS both
 *    when the current variant is available, so the modal's ATC state stays fully derivable
 *    from the variant-picker JSON on every update.
 *
 * Ordering: this enforcer runs synchronously on the `variantUpdate` bubble (document-level
 * listener, fires after `product-form-component`'s dialog-scoped listener starts). The
 * sync work in #onVariantUpdate (including morph) is already done before we run; the
 * later async `#setAddToCartLoading(false)` respects whatever state we leave behind.
 *
 * @param {HTMLElement | null | undefined} modalContent
 */
export function enforceQuickAddModalAtcAvailability(modalContent) {
  if (!modalContent) return;
  const picker = modalContent.querySelector('variant-picker');
  const jsonEl = picker?.querySelector('script[type="application/json"]');
  const rawJson = jsonEl?.textContent;
  if (!rawJson) return;
  /** @type {{ available?: boolean, price?: number | string } | null} */
  let variant = null;
  try {
    variant = JSON.parse(rawJson);
  } catch {
    return;
  }
  // `variant.price` is the retail price on TESO products, whose displayed
  // (promotional) price comes from a metafield, so trust the server-resolved
  // display price when the picker supplies it. An empty attribute means the
  // server found no valid price and the CTA must stay disabled.
  const rawDisplayPrice = jsonEl?.dataset.displayPrice;
  let price;
  if (rawDisplayPrice === undefined) {
    price = variant?.price == null ? NaN : Number(variant.price);
  } else {
    price = rawDisplayPrice === '' ? NaN : Number(rawDisplayPrice);
  }
  const isUnavailable = !variant || variant.available === false || !(price > 0);
  const btn = /** @type {HTMLButtonElement | null} */ (
    modalContent.querySelector('.buy-buttons-block .add-to-cart-button, .add-to-cart-button')
  );
  if (!btn) return;
  if (isUnavailable) {
    btn.disabled = true;
    btn.dataset.unavailable = 'true';
  } else {
    btn.disabled = false;
    delete btn.dataset.unavailable;
  }
}

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent) return;
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  const touchesQuickAdd = path.some(
    (n) => n === modalContent || (n instanceof Element && modalContent.contains(n))
  );
  if (!touchesQuickAdd) return;

  const dialog = document.getElementById('quick-add-dialog');
  const isKioskAppearance =
    modalContent.classList.contains('kiosk-quickadd--minimal') &&
    dialog?.classList.contains('bunnings-quickadd--kiosk-appearance');
  if (isKioskAppearance) {
    void queueBunningsQuickAddKioskAtcIcon(modalContent);
  }

  enforceQuickAddModalAtcAvailability(modalContent);
});

/**
 * Opens the theme quick-add dialog with PDP markup (variant picker + buy buttons).
 * @param {string} productPageUrl - Absolute product URL
 * @param {string} productTitle - Card title fallback for mobile header
 * @param {string} [presetVariantId] - Optional variant to sync from the card hidden input
 * @param {{ kioskAppearance?: boolean }} [options] - kioskAppearance: match kiosk quick-add (prune, styling, icon+label ATC)
 * @returns {Promise<boolean>} Whether the modal opened
 */
export async function openQuickAddModalForProductPage(productPageUrl, productTitle, presetVariantId, options = {}) {
  const dialog = document.getElementById('quick-add-dialog');
  const kioskAppearance = options.kioskAppearance === true;

  if (dialog) {
    if (kioskAppearance) {
      dialog.classList.add('bunnings-quickadd--kiosk-appearance');
    } else {
      dialog.classList.remove('bunnings-quickadd--kiosk-appearance');
    }
  }

  const modalContentEl = document.getElementById('quick-add-modal-content');
  if (!kioskAppearance) {
    modalContentEl?.classList.remove('kiosk-quickadd--minimal');
  }

  const gridClone = await fetchProductGridCloneForQuickAdd(productPageUrl);
  if (!gridClone) return false;

  setQuickAddModalHeadingFromGridClone(gridClone);
  const prepared = prepareProductGridCloneForQuickAddModal(gridClone, productPageUrl, productTitle);
  morphIntoQuickAddModal(prepared);

  const modalContent = document.getElementById('quick-add-modal-content');
  syncQuickAddModalVariantFromId(modalContent, presetVariantId);
  wireQuickAddVariantIdSync(modalContent);

  if (kioskAppearance) {
    pruneQuickAddModalToKioskMinimal(modalContent);
    await applyBunningsQuickAddKioskAtcIcon(modalContent);
  }

  enforceQuickAddModalAtcAvailability(modalContent);

  showQuickAddDialogWithScrollReset();
  return true;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const prepared = prepareProductGridCloneForQuickAddModal(
      productGrid,
      this.productPageUrl,
      this.dataset.productTitle || ''
    );

    morph(modalContent, prepared);

    this.#syncVariantSelection(modalContent);

    enforceQuickAddModalAtcAvailability(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    // Keep quick-add open for cart quantity adjustments; close only on successful add-to-cart.
    const isAddToCartEvent =
      event instanceof CartAddEvent || Boolean(event?.detail?.data?.sections);
    if (!isAddToCartEvent) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    this.classList.remove('bunnings-quickadd--kiosk-appearance');
    document.getElementById('quick-add-modal-content')?.classList.remove('kiosk-quickadd--minimal');

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

if (typeof window !== 'undefined') {
  window.BunningsQuickAdd = {
    ...(window.BunningsQuickAdd || {}),
    openQuickAddModalForProductPage,
  };
}
